<template>
  <div class="sessaoHistoricoDetalhado">
    <header class="sessaoHistoricoCabecalho">
      <div>
        <span>Análise por período</span>
        <h2>Histórico Geral</h2>
        <small>Consolidação das sessões em períodos de até 2 dias.</small>
      </div>

      <div class="sessaoHistoricoFiltros">
        <label>
          <span>Início</span>
          <input
            v-model="dataInicio"
            type="date"
            :disabled="carregando"
            :min="dataInicioMinima"
            :max="dataFim"
          />
        </label>
        <label>
          <span>Fim</span>
          <input
            v-model="dataFim"
            type="date"
            :disabled="carregando"
            :min="dataInicio"
            :max="dataFimMaxima"
          />
        </label>
        <button
          type="button"
          class="sessaoHistoricoPesquisar"
          :disabled="carregando"
          @click="carregarItens"
        >
          <i class="fa fa-search" aria-hidden="true"></i>
          Consultar
        </button>
      </div>
    </header>

    <div class="sessaoHistoricoAtalhos">
      <span>Período rápido</span>
      <button type="button" :disabled="carregando" @click="definirPeriodo(1)">
        Hoje
      </button>
      <button type="button" :disabled="carregando" @click="definirPeriodo(2)">
        2 dias
      </button>
    </div>

    <div v-if="carregando" class="sessaoHistoricoProcessando" role="status">
      <i class="fa fa-refresh fa-spin" aria-hidden="true"></i>
      <span>
        Processando o histórico em pequenos lotes
        <small>O tempo varia conforme o volume de sessões encontrado.</small>
      </span>
    </div>

    <div v-if="erro" class="sessaoHistoricoEstado sessaoHistoricoErro">
      <i class="fa fa-exclamation-circle" aria-hidden="true"></i>
      <span>{{ erro }}</span>
      <button type="button" @click="carregarItens">Tentar novamente</button>
    </div>

    <div v-else-if="carregando && !carregado" class="sessaoHistoricoEstado">
      <i class="fa fa-clock-o" aria-hidden="true"></i>
      <span>Aguardando a conclusão da consulta</span>
    </div>

    <div v-else-if="!consultado" class="sessaoHistoricoEstado">
      <i class="fa fa-calendar-o" aria-hidden="true"></i>
      <span>Selecione o período e clique em Consultar.</span>
    </div>

    <template v-else>
      <section class="sessaoHistoricoMetricas" aria-label="Totais do período">
        <article>
          <span class="sessaoHistoricoIcone sessaoHistoricoIconeVerde">
            <i class="fa fa-play-circle" aria-hidden="true"></i>
          </span>
          <div>
            <small>Sessões</small>
            <strong>{{ formatarNumero(totais.sessoes) }}</strong>
          </div>
        </article>
        <article>
          <span class="sessaoHistoricoIcone sessaoHistoricoIconeAzul">
            <i class="fa fa-users" aria-hidden="true"></i>
          </span>
          <div>
            <small>Usuários</small>
            <strong>{{ formatarNumero(totais.usuarios) }}</strong>
          </div>
        </article>
        <article>
          <span class="sessaoHistoricoIcone sessaoHistoricoIconeAmarelo">
            <i class="fa fa-calendar" aria-hidden="true"></i>
          </span>
          <div>
            <small>Eventos</small>
            <strong>{{ formatarNumero(totais.eventos) }}</strong>
          </div>
        </article>
        <article>
          <span class="sessaoHistoricoIcone sessaoHistoricoIconeVioleta">
            <i class="fa fa-globe" aria-hidden="true"></i>
          </span>
          <div>
            <small>IPs utilizados</small>
            <strong>{{ formatarNumero(totais.ips) }}</strong>
          </div>
        </article>
        <article>
          <span class="sessaoHistoricoIcone sessaoHistoricoIconeRosa">
            <i class="fa fa-hourglass-half" aria-hidden="true"></i>
          </span>
          <div>
            <small>Média por sessão</small>
            <strong>{{ formatarDuracao(totais.duracaoMediaMinutos) }}</strong>
          </div>
        </article>
      </section>

      <article class="sessaoHistoricoWidget sessaoHistoricoEvolucao">
        <header>
          <div>
            <span>Movimento no período</span>
            <h3>Sessões por dia</h3>
          </div>
          <strong>{{ porDia.length }}</strong>
        </header>
        <div v-if="porDia.length" class="sessaoHistoricoDias">
          <div
            v-for="dia in porDia"
            :key="dia.data"
            class="sessaoHistoricoDia"
            :title="descricaoDia(dia)"
          >
            <strong>{{ dia.sessoes }}</strong>
            <span class="sessaoHistoricoBarraVertical">
              <span :style="{ height: percentualDia(dia.sessoes) + '%' }"></span>
            </span>
            <small>{{ formatarDiaCurto(dia.data) }}</small>
          </div>
        </div>
        <div v-else class="sessaoHistoricoVazio sessaoHistoricoVazioCompacto">
          <i class="fa fa-calendar-o" aria-hidden="true"></i>
          <span>Nenhuma sessão iniciada no período</span>
        </div>
      </article>

      <section class="sessaoHistoricoGrade">
        <article class="sessaoHistoricoWidget">
          <header>
            <div>
              <span>Audiência consolidada</span>
              <h3>Sessões por evento</h3>
            </div>
            <strong>{{ eventos.length }}</strong>
          </header>
          <div class="sessaoHistoricoTabelaCabecalho sessaoHistoricoEventoGrade">
            <span>Evento</span>
            <span>Usuários</span>
            <span>IPs</span>
            <span>Sessões</span>
          </div>
          <div v-if="eventos.length" class="sessaoHistoricoLista">
            <div
              v-for="(evento, indice) in eventos"
              :key="evento._id"
              class="sessaoHistoricoLinha sessaoHistoricoEventoGrade"
            >
              <div class="sessaoHistoricoIdentidade">
                <span class="sessaoHistoricoPosicao">{{ indice + 1 }}</span>
                <span>
                  <strong>{{ evento.nome }}</strong>
                  <small>
                    {{ detalheEvento(evento) }}
                  </small>
                </span>
              </div>
              <strong>{{ formatarNumero(evento.usuarios) }}</strong>
              <strong>{{ formatarNumero(evento.ips) }}</strong>
              <strong class="sessaoHistoricoTotal">{{ formatarNumero(evento.sessoes) }}</strong>
            </div>
          </div>
          <div v-else class="sessaoHistoricoVazio">
            <i class="fa fa-calendar-o" aria-hidden="true"></i>
            <span>Nenhum evento encontrado</span>
          </div>
        </article>

        <article class="sessaoHistoricoWidget">
          <header>
            <div>
              <span>Origem dos acessos</span>
              <h3>IPs utilizados</h3>
            </div>
            <strong>{{ ips.length }}</strong>
          </header>
          <div class="sessaoHistoricoTabelaCabecalho sessaoHistoricoIpGrade">
            <span>IP</span>
            <span>Usuários</span>
            <span>Eventos</span>
            <span>Sessões</span>
          </div>
          <div v-if="ips.length" class="sessaoHistoricoLista">
            <div
              v-for="ip in ips"
              :key="ip._id"
              class="sessaoHistoricoLinha sessaoHistoricoIpGrade"
            >
              <span class="sessaoHistoricoIp">
                <i class="fa fa-globe" aria-hidden="true"></i>
                <strong>{{ ip.ip }}</strong>
              </span>
              <strong>{{ formatarNumero(ip.usuarios) }}</strong>
              <strong>{{ formatarNumero(ip.eventos) }}</strong>
              <strong class="sessaoHistoricoTotal">{{ formatarNumero(ip.sessoes) }}</strong>
            </div>
          </div>
          <div v-else class="sessaoHistoricoVazio">
            <i class="fa fa-globe" aria-hidden="true"></i>
            <span>Nenhum IP encontrado</span>
          </div>
        </article>
      </section>

      <article class="sessaoHistoricoWidget sessaoHistoricoUsuarios">
        <header>
          <div>
            <span>Participação no período</span>
            <h3>Ranking de usuários</h3>
          </div>
          <strong>{{ usuarios.length }}</strong>
        </header>
        <div class="sessaoHistoricoTabelaCabecalho sessaoHistoricoUsuarioGrade">
          <span>Usuário</span>
          <span>Eventos</span>
          <span>IPs</span>
          <span>Última atividade</span>
          <span>Sessões</span>
        </div>
        <div v-if="usuarios.length" class="sessaoHistoricoLista">
          <div
            v-for="(usuario, indice) in usuarios"
            :key="usuario._id"
            class="sessaoHistoricoLinha sessaoHistoricoUsuarioGrade"
          >
            <div class="sessaoHistoricoIdentidade">
              <span class="sessaoHistoricoPosicao">{{ indice + 1 }}</span>
              <span>
                <strong>{{ usuario.email }}</strong>
                <small>{{ usuario.site || "Sem site" }}</small>
              </span>
            </div>
            <strong>{{ formatarNumero(usuario.eventos) }}</strong>
            <strong>{{ formatarNumero(usuario.ips) }}</strong>
            <small>{{ formatarDataHora(usuario.ultimaAtividade) }}</small>
            <strong class="sessaoHistoricoTotal">{{ formatarNumero(usuario.sessoes) }}</strong>
          </div>
        </div>
        <div v-else class="sessaoHistoricoVazio">
          <i class="fa fa-user-o" aria-hidden="true"></i>
          <span>Nenhum usuário encontrado</span>
        </div>
      </article>
    </template>
  </div>
</template>

<script>
import { api } from "@/services/api.js";

function totaisVazios() {
  return {
    sessoes: 0,
    usuarios: 0,
    eventos: 0,
    ips: 0,
    sites: 0,
    duracaoMediaMinutos: 0,
  };
}

export default {
  name: "ListSessaoViewHistoricoDetalhado",
  props: {
    site: { type: String, default: "Todos" },
  },
  computed: {
    dataFimMaxima() {
      if (!this.dataInicio) return "";

      var data = new Date(this.dataInicio + "T00:00:00");
      data.setDate(data.getDate() + 1);
      return this.dataParaInput(data);
    },
    dataInicioMinima() {
      if (!this.dataFim) return "";

      var data = new Date(this.dataFim + "T00:00:00");
      data.setDate(data.getDate() - 1);
      return this.dataParaInput(data);
    },
    maiorSessoesDia() {
      return this.porDia.reduce(
        (maior, dia) => Math.max(maior, Number(dia.sessoes || 0)),
        0
      );
    },
  },
  watch: {
    site() {
      if (this.carregando) {
        this.recarregarPendente = true;
      } else if (this.carregado) {
        this.carregarItens();
      }
    },
  },
  methods: {
    async carregarItens() {
      if (this.carregando) return;

      if (!this.dataInicio || !this.dataFim) {
        this.erro = "Informe as duas datas para consultar o histórico.";
        return;
      }

      if (this.dataInicio > this.dataFim) {
        this.erro = "A data inicial deve ser anterior à data final.";
        return;
      }

      var requisicao = ++this.requisicaoAtual;
      var inicio = new Date(this.dataInicio + "T00:00:00.000");
      var fim = new Date(this.dataFim + "T23:59:59.999");

      if (fim.getTime() - inicio.getTime() > 2 * 24 * 60 * 60 * 1000) {
        this.erro = "O período máximo para a consulta é de 2 dias.";
        return;
      }

      var filtros = [
        "inicio=" + encodeURIComponent(inicio.toISOString()),
        "fim=" + encodeURIComponent(fim.toISOString()),
        "_limit=500",
      ];

      if (this.site && this.site !== "Todos") {
        filtros.push("site=" + encodeURIComponent(this.site));
      }

      this.carregando = true;
      this.erro = "";
      this.fonteCancelamento = api.cancelTokenSource();

      try {
        var response = await api.get(
          "/sessaoView/historico/detalhado?" + filtros.join("&"),
          { cancelToken: this.fonteCancelamento.token }
        );
        api.valid(response.data, this.$router);

        if (response.data.erro) {
          throw new Error(
            response.data.mensagem || "Não foi possível calcular o histórico."
          );
        }

        if (requisicao !== this.requisicaoAtual) return;

        this.totais = Object.assign(totaisVazios(), response.data.totais || {});
        this.eventos = response.data.eventos || [];
        this.usuarios = response.data.usuarios || [];
        this.ips = response.data.ips || [];
        this.porDia = response.data.porDia || [];
        this.carregado = true;
        this.consultado = true;
      } catch (erro) {
        if (requisicao !== this.requisicaoAtual) return;

        if (api.isCancel(erro)) return;

        this.erro =
          erro && erro.message
            ? erro.message
            : "Não foi possível calcular o histórico.";
      } finally {
        if (requisicao === this.requisicaoAtual) {
          this.carregando = false;
          this.fonteCancelamento = null;

          if (this.recarregarPendente) {
            this.recarregarPendente = false;
            this.$nextTick(this.carregarItens);
          }
        }
      }
    },
    definirPeriodo(dias) {
      var fim = new Date();
      var inicio = new Date();

      inicio.setDate(inicio.getDate() - (dias - 1));
      this.dataInicio = this.dataParaInput(inicio);
      this.dataFim = this.dataParaInput(fim);
    },
    dataParaInput(data) {
      var ano = data.getFullYear();
      var mes = String(data.getMonth() + 1).padStart(2, "0");
      var dia = String(data.getDate()).padStart(2, "0");

      return ano + "-" + mes + "-" + dia;
    },
    detalheEvento(evento) {
      var detalhes = [];
      var data = this.formatarDataEvento(evento);

      if (data) detalhes.push(data);
      if (evento.competicao) detalhes.push(evento.competicao);
      if (evento.site) detalhes.push(evento.site);
      return detalhes.join(" · ") || "Sem detalhes adicionais";
    },
    formatarDataEvento(evento) {
      if (!evento.dataAno || !evento.dataMes || !evento.dataDia) return "";

      return (
        String(evento.dataDia).padStart(2, "0") +
        "/" +
        String(evento.dataMes).padStart(2, "0") +
        "/" +
        evento.dataAno +
        (evento.dataHora !== undefined
          ? " " +
            String(evento.dataHora).padStart(2, "0") +
            ":" +
            String(evento.dataMinuto || 0).padStart(2, "0")
          : "")
      );
    },
    formatarDataHora(data) {
      if (!data) return "-";
      return new Intl.DateTimeFormat("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      }).format(new Date(data));
    },
    formatarDiaCurto(data) {
      var partes = data.split("-");
      return partes[2] + "/" + partes[1];
    },
    formatarNumero(valor) {
      return Number(valor || 0).toLocaleString("pt-BR");
    },
    formatarDuracao(minutos) {
      var total = Number(minutos || 0);
      if (total < 60) return total + " min";

      var horas = Math.floor(total / 60);
      var restante = total % 60;
      if (horas < 24) return horas + "h" + (restante ? " " + restante + "min" : "");

      var dias = Math.floor(horas / 24);
      return dias + "d " + (horas % 24) + "h";
    },
    percentualDia(valor) {
      if (!this.maiorSessoesDia) return 0;
      return Math.max(5, (Number(valor || 0) / this.maiorSessoesDia) * 100);
    },
    descricaoDia(dia) {
      return (
        this.formatarDiaCurto(dia.data) +
        ": " +
        dia.sessoes +
        " sessões, " +
        dia.usuarios +
        " usuários, " +
        dia.eventos +
        " eventos"
      );
    },
  },
  mounted() {
    this.definirPeriodo(2);
  },
  beforeDestroy() {
    if (this.fonteCancelamento) {
      this.fonteCancelamento.cancel("Consulta histórica encerrada.");
    }
  },
  data() {
    return {
      carregado: false,
      carregando: false,
      consultado: false,
      dataFim: "",
      dataInicio: "",
      erro: "",
      eventos: [],
      fonteCancelamento: null,
      ips: [],
      porDia: [],
      recarregarPendente: false,
      requisicaoAtual: 0,
      totais: totaisVazios(),
      usuarios: [],
    };
  },
};
</script>

<style>
.sessaoHistoricoDetalhado {
  color: #293138;
}

.sessaoHistoricoCabecalho {
  align-items: flex-end;
  background: #ffffff;
  border: 1px solid #dfe3e7;
  display: flex;
  gap: 20px;
  justify-content: space-between;
  padding: 14px 16px;
}

.sessaoHistoricoCabecalho > div:first-child > span,
.sessaoHistoricoWidget > header span {
  color: #398063;
  display: block;
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
}

.sessaoHistoricoCabecalho h2 {
  font-size: 15px;
  font-weight: 600;
  margin: 2px 0 3px;
}

.sessaoHistoricoCabecalho small {
  color: #737d85;
  font-size: 10px;
}

.sessaoHistoricoFiltros {
  align-items: flex-end;
  display: flex;
  gap: 8px;
}

.sessaoHistoricoFiltros label {
  margin: 0;
}

.sessaoHistoricoFiltros label > span,
.sessaoHistoricoAtalhos > span {
  color: #69747c;
  display: block;
  font-size: 8px;
  font-weight: 700;
  margin-bottom: 3px;
  text-transform: uppercase;
}

.sessaoHistoricoFiltros input {
  background: #ffffff;
  border: 1px solid #cbd2d7;
  color: inherit;
  font-size: 10px;
  height: 34px;
  padding: 0 8px;
  width: 132px;
}

.sessaoHistoricoPesquisar {
  align-items: center;
  background: #287d59;
  border: 1px solid #287d59;
  color: #ffffff;
  display: inline-flex;
  font-size: 10px;
  gap: 6px;
  height: 34px;
  padding: 0 12px;
}

.sessaoHistoricoPesquisar:disabled {
  cursor: wait;
  opacity: 0.65;
}

.sessaoHistoricoAtalhos {
  align-items: center;
  background: #f5f7f8;
  border: 1px solid #dfe3e7;
  border-top: 0;
  display: flex;
  gap: 6px;
  min-height: 46px;
  padding: 6px 10px;
}

.sessaoHistoricoAtalhos > span {
  margin: 0 4px 0 0;
}

.sessaoHistoricoAtalhos > button {
  background: #ffffff;
  border: 1px solid #cbd2d7;
  color: #4e5961;
  font-size: 9px;
  height: 28px;
  padding: 0 9px;
}

.sessaoHistoricoAtalhos > button:disabled,
.sessaoHistoricoFiltros input:disabled {
  cursor: wait;
  opacity: 0.55;
}

.sessaoHistoricoProcessando {
  align-items: center;
  background: #edf5f1;
  border: 1px solid #cbded5;
  color: #2e6f54;
  display: flex;
  font-size: 10px;
  gap: 9px;
  margin-top: 10px;
  min-height: 42px;
  padding: 7px 11px;
}

.sessaoHistoricoProcessando > i {
  font-size: 14px;
}

.sessaoHistoricoProcessando span,
.sessaoHistoricoProcessando small {
  display: block;
}

.sessaoHistoricoProcessando small {
  color: #6f8078;
  font-size: 8px;
  margin-top: 1px;
}

.sessaoHistoricoMetricas {
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  margin: 12px 0;
}

.sessaoHistoricoMetricas article {
  align-items: center;
  background: #ffffff;
  border: 1px solid #dfe3e7;
  display: flex;
  gap: 9px;
  min-height: 66px;
  min-width: 0;
  padding: 9px 11px;
}

.sessaoHistoricoMetricas small,
.sessaoHistoricoMetricas strong {
  display: block;
}

.sessaoHistoricoMetricas small {
  color: #707a82;
  font-size: 8px;
  font-weight: 600;
  line-height: 1.25;
}

.sessaoHistoricoMetricas strong {
  font-size: 17px;
  font-weight: 600;
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sessaoHistoricoIcone {
  align-items: center;
  display: inline-flex;
  flex: 0 0 31px;
  height: 31px;
  justify-content: center;
}

.sessaoHistoricoIconeVerde { background: #e4f4eb; color: #27805a; }
.sessaoHistoricoIconeAzul { background: #e7f0f7; color: #386f97; }
.sessaoHistoricoIconeAmarelo { background: #fff4d8; color: #9b7417; }
.sessaoHistoricoIconeVioleta { background: #ede8f5; color: #735893; }
.sessaoHistoricoIconeCinza { background: #e9edef; color: #52616b; }
.sessaoHistoricoIconeRosa { background: #f5e8ee; color: #945675; }

.sessaoHistoricoWidget {
  background: #ffffff;
  border: 1px solid #dfe3e7;
  min-width: 0;
}

.sessaoHistoricoWidget > header {
  align-items: flex-start;
  border-bottom: 1px solid #e3e7ea;
  display: flex;
  justify-content: space-between;
  padding: 11px 13px 9px;
}

.sessaoHistoricoWidget h3 {
  font-size: 13px;
  font-weight: 600;
  margin: 2px 0 0;
}

.sessaoHistoricoWidget > header > strong {
  background: #edf0f2;
  color: #4f5962;
  font-size: 9px;
  min-width: 28px;
  padding: 3px 7px;
  text-align: center;
}

.sessaoHistoricoEvolucao {
  margin-bottom: 12px;
}

.sessaoHistoricoDias {
  align-items: flex-end;
  display: flex;
  gap: 6px;
  min-height: 122px;
  overflow-x: auto;
  padding: 12px 14px 10px;
}

.sessaoHistoricoDia {
  align-items: center;
  display: flex;
  flex: 1 0 34px;
  flex-direction: column;
  gap: 4px;
  height: 96px;
  justify-content: flex-end;
  min-width: 34px;
}

.sessaoHistoricoDia > strong {
  font-size: 8px;
}

.sessaoHistoricoDia > small {
  color: #747e86;
  font-size: 7px;
}

.sessaoHistoricoBarraVertical {
  align-items: flex-end;
  background: #edf0f2;
  display: flex;
  height: 62px;
  width: 14px;
}

.sessaoHistoricoBarraVertical > span {
  background: #3a8b68;
  display: block;
  transition: height 0.2s ease;
  width: 100%;
}

.sessaoHistoricoGrade {
  display: grid;
  gap: 12px;
  grid-template-columns: minmax(0, 1.35fr) minmax(390px, 0.65fr);
}

.sessaoHistoricoUsuarios {
  margin-top: 12px;
}

.sessaoHistoricoTabelaCabecalho {
  background: #f3f5f6;
  border-bottom: 1px solid #dfe3e7;
  color: #69737b;
  font-size: 8px;
  font-weight: 700;
  min-height: 30px;
  text-transform: uppercase;
}

.sessaoHistoricoEventoGrade,
.sessaoHistoricoIpGrade,
.sessaoHistoricoUsuarioGrade {
  align-items: center;
  display: grid;
  gap: 10px;
  padding: 0 12px;
}

.sessaoHistoricoEventoGrade {
  grid-template-columns: minmax(190px, 1fr) 58px 45px 58px;
}

.sessaoHistoricoIpGrade {
  grid-template-columns: minmax(130px, 1fr) 55px 52px 58px;
}

.sessaoHistoricoUsuarioGrade {
  grid-template-columns: minmax(210px, 1fr) 60px 48px 105px 58px;
}

.sessaoHistoricoTabelaCabecalho > span:not(:first-child) {
  text-align: right;
}

.sessaoHistoricoLista {
  max-height: 460px;
  overflow-y: auto;
}

.sessaoHistoricoLinha {
  border-bottom: 1px solid #edf0f2;
  min-height: 52px;
}

.sessaoHistoricoLinha > strong,
.sessaoHistoricoLinha > small {
  text-align: right;
}

.sessaoHistoricoLinha > small {
  color: #78828a;
  font-size: 8px;
}

.sessaoHistoricoIdentidade {
  align-items: center;
  display: flex;
  gap: 8px;
  min-width: 0;
}

.sessaoHistoricoIdentidade > span:last-child {
  min-width: 0;
}

.sessaoHistoricoIdentidade strong,
.sessaoHistoricoIdentidade small {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sessaoHistoricoIdentidade strong {
  font-size: 10px;
}

.sessaoHistoricoIdentidade small {
  color: #78828a;
  font-size: 8px;
  margin-top: 2px;
}

.sessaoHistoricoPosicao {
  align-items: center;
  background: #edf0f2;
  color: #52606a;
  display: inline-flex;
  flex: 0 0 23px;
  font-size: 8px;
  height: 23px;
  justify-content: center;
}

.sessaoHistoricoIp {
  align-items: center;
  display: flex;
  font-size: 10px;
  gap: 6px;
  min-width: 0;
}

.sessaoHistoricoIp strong {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sessaoHistoricoTotal {
  color: #287d59;
  font-size: 13px;
}

.sessaoHistoricoEstado,
.sessaoHistoricoVazio {
  align-items: center;
  color: #7b858d;
  display: flex;
  flex-direction: column;
  font-size: 10px;
  gap: 8px;
  justify-content: center;
  min-height: 150px;
}

.sessaoHistoricoEstado {
  background: #ffffff;
  border: 1px solid #dfe3e7;
  margin-top: 12px;
}

.sessaoHistoricoEstado i,
.sessaoHistoricoVazio i {
  font-size: 18px;
}

.sessaoHistoricoVazioCompacto {
  min-height: 120px;
}

.sessaoHistoricoErro {
  color: #a14545;
}

.sessaoHistoricoErro button {
  background: transparent;
  border: 1px solid #b96a6a;
  color: inherit;
  font-size: 9px;
  padding: 5px 9px;
}

.body-dark .sessaoHistoricoDetalhado,
.body-dark .sessaoHistoricoFiltros input {
  color: var(--dark-text-soft) !important;
}

.body-dark .sessaoHistoricoCabecalho,
.body-dark .sessaoHistoricoMetricas article,
.body-dark .sessaoHistoricoWidget,
.body-dark .sessaoHistoricoEstado,
.body-dark .sessaoHistoricoAtalhos > button,
.body-dark .sessaoHistoricoFiltros input {
  background: var(--dark-surface) !important;
  border-color: var(--dark-border) !important;
}

.body-dark .sessaoHistoricoAtalhos,
.body-dark .sessaoHistoricoTabelaCabecalho,
.body-dark .sessaoHistoricoWidget > header > strong,
.body-dark .sessaoHistoricoPosicao,
.body-dark .sessaoHistoricoBarraVertical {
  background: var(--dark-surface-alt) !important;
  color: var(--dark-text-soft) !important;
}

.body-dark .sessaoHistoricoProcessando {
  background: rgba(57, 128, 99, 0.12) !important;
  border-color: rgba(74, 156, 122, 0.32) !important;
  color: #69bd99 !important;
}

.body-dark .sessaoHistoricoProcessando small {
  color: var(--dark-muted) !important;
}

.body-dark .sessaoHistoricoCabecalho,
.body-dark .sessaoHistoricoAtalhos,
.body-dark .sessaoHistoricoWidget > header,
.body-dark .sessaoHistoricoTabelaCabecalho,
.body-dark .sessaoHistoricoLinha {
  border-color: var(--dark-border-soft) !important;
}

.body-dark .sessaoHistoricoCabecalho small,
.body-dark .sessaoHistoricoMetricas small,
.body-dark .sessaoHistoricoIdentidade small,
.body-dark .sessaoHistoricoLinha > small,
.body-dark .sessaoHistoricoDia > small,
.body-dark .sessaoHistoricoVazio {
  color: var(--dark-muted) !important;
}

@media (max-width: 1200px) {
  .sessaoHistoricoMetricas {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .sessaoHistoricoGrade {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 767px) {
  .sessaoHistoricoCabecalho,
  .sessaoHistoricoFiltros {
    align-items: stretch;
    flex-direction: column;
  }

  .sessaoHistoricoFiltros input,
  .sessaoHistoricoPesquisar {
    width: 100%;
  }

  .sessaoHistoricoAtalhos {
    flex-wrap: wrap;
  }

  .sessaoHistoricoMetricas {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .sessaoHistoricoTabelaCabecalho {
    display: none;
  }

  .sessaoHistoricoEventoGrade,
  .sessaoHistoricoIpGrade,
  .sessaoHistoricoUsuarioGrade {
    grid-template-columns: minmax(0, 1fr) 54px;
  }

  .sessaoHistoricoLinha > :not(:first-child):not(:last-child) {
    display: none;
  }
}
</style>
